import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useLanguage, dateLocale } from './i18n';
import './AdminDashboard.css';
import BlacklistManager from './BlacklistManager';
import AdminEventManager from './AdminEventManager';
import AnalyticsCharts from './AnalyticsCharts';
import ServiceManager from './ServiceManager';
import ServiceKeyManager from './ServiceKeyManager';
import { useBackClose } from './backButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Категории с правильными названиями (двуязычно)
const CATEGORY_NAMES = {
  'emergency': { icon: '🚨', ru: 'Экстренная ситуация', kk: 'Төтенше жағдай' },
  'municipal': { icon: '🏢', ru: 'ЖКХ проблемы', kk: 'ТКШ мәселелері' },
  'architecture': { icon: '🏗', ru: 'Градостроительство', kk: 'Қала құрылысы' },
  'heating': { icon: '🔥', ru: 'Отопление', kk: 'Жылыту' },
  'electricity': { icon: '⚡', ru: 'Электроснабжение', kk: 'Электрмен жабдықтау' },
  'telecom': { icon: '📞', ru: 'Связь и интернет', kk: 'Байланыс және интернет' },
  'traffic_police': { icon: '🚔', ru: 'Дорожные проблемы', kk: 'Жол мәселелері' },
  'police': { icon: '👮', ru: 'Общественный порядок', kk: 'Қоғамдық тәртіп' },
  'improvement': { icon: '🌳', ru: 'Благоустройство', kk: 'Абаттандыру' },
  'education': { icon: '🎓', ru: 'Образование', kk: 'Білім беру' },
  'culture_sport': { icon: '🎨', ru: 'Культура и спорт', kk: 'Мәдениет және спорт' },
  'waste': { icon: '🗑', ru: 'Вывоз мусора', kk: 'Қоқыс шығару' },
  'healthcare': { icon: '🏥', ru: 'Здравоохранение', kk: 'Денсаулық сақтау' },
  'lighting': { icon: '💡', ru: 'Уличное освещение', kk: 'Көше жарығы' }
};

const STATUS_NAMES = {
  'new': { ru: 'Новое', kk: 'Жаңа' },
  'assigned': { ru: 'Назначено', kk: 'Тағайындалды' },
  'in_progress': { ru: 'В работе', kk: 'Орындалуда' },
  'completed': { ru: 'Выполнено', kk: 'Орындалды' },
  'final_completed': { ru: 'Завершено', kk: 'Аяқталды' },
  'rejected': { ru: 'Отклонено', kk: 'Қабылданбады' }
};

const AD_T = {
  kk: {
    loading: 'Дашборд жүктелуде...',
    errorTitle: 'Деректерді жүктеу қатесі',
    retry: 'Қайталау',
    title: 'Алтай қаласын басқару панелі',
    subtitle: 'Оқиғалар статистикасы мен аналитикасы',
    exportPdf: 'PDF-ке экспорттау',
    exportExcel: 'Excel-ге экспорттау',
    exportError: 'Экспорт қатесі. Кейінірек қайталап көріңіз.',
    exportExcelError: 'Excel-ге экспорт қатесі. Кейінірек қайталап көріңіз.',
    loadError: 'Дашборд деректерін жүктеу қатесі',
    close: 'Жабу',
    totalEvents: 'Барлық оқиғалар',
    inProgress: 'Орындалуда',
    emergency: 'Төтенше',
    needsAttention: 'Назар қажет',
    completed: 'Орындалды',
    statusDistribution: '📊 Мәртебелер бойынша бөлу',
    topCategories: '🏆 Танымал санаттар',
    eventsCount: 'оқиға',
    recentEvents: '🕐 Соңғы оқиғалар',
    noDescription: 'Сипаттамасыз',
    quickActions: '⚡ Жылдам әрекеттер',
    eventManagement: 'Оқиғаларды басқару',
    blacklist: 'Қара тізім',
    serviceManagement: 'Қызметтерді басқару',
    serviceKeys: 'Қызмет кілттері',
    allEvents: 'Барлық оқиғалар'
  },
  ru: {
    loading: 'Загрузка дашборда...',
    errorTitle: 'Ошибка загрузки данных',
    retry: 'Повторить',
    title: 'Панель управления города Алтай',
    subtitle: 'Статистика и аналитика событий',
    exportPdf: 'Экспорт в PDF',
    exportExcel: 'Экспорт в Excel',
    exportError: 'Ошибка экспорта. Попробуйте позже.',
    exportExcelError: 'Ошибка экспорта в Excel. Попробуйте позже.',
    loadError: 'Ошибка загрузки данных дашборда',
    close: 'Закрыть',
    totalEvents: 'Всего событий',
    inProgress: 'В работе',
    emergency: 'Экстренных',
    needsAttention: 'Требует внимания',
    completed: 'Выполнено',
    statusDistribution: '📊 Распределение по статусам',
    topCategories: '🏆 Популярные категории',
    eventsCount: 'событий',
    recentEvents: '🕐 Последние события',
    noDescription: 'Без описания',
    quickActions: '⚡ Быстрые действия',
    eventManagement: 'Управление событиями',
    blacklist: 'Черный список',
    serviceManagement: 'Управление службами',
    serviceKeys: 'Ключи служб',
    allEvents: 'Все события'
  }
};

const AdminDashboard = ({ onClose, onOpenEventFeed, onSelectEvent }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = AD_T[language];
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBlacklistManager, setShowBlacklistManager] = useState(false);
  const [showEventManagement, setShowEventManagement] = useState(false);
  const [showServiceManager, setShowServiceManager] = useState(false);
  const [showKeyManager, setShowKeyManager] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Кнопка «Назад» закрывает открытое модальное окно дашборда
  useBackClose(showBlacklistManager, () => setShowBlacklistManager(false));
  useBackClose(showEventManagement, () => setShowEventManagement(false));
  useBackClose(showServiceManager, () => setShowServiceManager(false));
  useBackClose(showKeyManager, () => setShowKeyManager(false));

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleExportPDF = async () => {
    try {
      setExportLoading(true);
      const response = await axios.get(`${API}/export/events/pdf`, {
        responseType: 'blob'
      });
      
      // Создаём ссылку для скачивания
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `turgyn_events_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert(t.exportError);
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);

      const response = await axios.get(`${API}/export/events/excel`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `turgyn_events_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export error:', err);
      alert(t.exportExcelError);
    } finally {
      setExportLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      const errorMsg = err.response?.data?.detail || err.message || t.loadError;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': '#2196F3',
      'assigned': '#9C27B0',
      'in_progress': '#FF9800',
      'completed': '#4CAF50',
      'final_completed': '#2E7D32',
      'rejected': '#F44336'
    };
    return colors[status] || '#757575';
  };

  const getCategoryName = (categoryId) => {
    const c = CATEGORY_NAMES[categoryId];
    return c ? `${c.icon} ${c[language]}` : `📍 ${categoryId}`;
  };

  const handleMetricClick = (filterType) => {
    onClose();
    if (onOpenEventFeed) {
      switch (filterType) {
        case 'all':
          onOpenEventFeed(null, 'all');
          break;
        case 'active':
          onOpenEventFeed(null, 'in_progress');
          break;
        case 'emergency':
          onOpenEventFeed(null, 'emergency');
          break;
        case 'completed':
          onOpenEventFeed(null, 'completed');
          break;
        default:
          onOpenEventFeed(null, 'all');
      }
    }
  };

  const handleCategoryClick = (categoryId) => {
    onClose();
    if (onOpenEventFeed) {
      onOpenEventFeed(categoryId);
    }
  };

  const handleEventClick = (eventId) => {
    onClose();
    if (onSelectEvent) {
      onSelectEvent(eventId);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(dateLocale(language), {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate completion rate
  const getCompletionRate = () => {
    if (!stats || !stats.total_events || stats.total_events === 0) return 0;
    const completed = stats.status_distribution?.find(s => s.status === 'completed')?.count || 0;
    const finalCompleted = stats.status_distribution?.find(s => s.status === 'final_completed')?.count || 0;
    return Math.round(((completed + finalCompleted) / stats.total_events) * 100);
  };

  // Calculate response rate
  const getResponseRate = () => {
    if (!stats || !stats.total_events || stats.total_events === 0) return 0;
    const newEvents = stats.status_distribution?.find(s => s.status === 'new')?.count || 0;
    return Math.round(((stats.total_events - newEvents) / stats.total_events) * 100);
  };

  if (loading) {
    return (
      <div className="dashboard-overlay">
        <div className="dashboard-panel-md">
          <div className="dashboard-loading">
            <div className="spinner-md"></div>
            <p>{t.loading}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="dashboard-overlay">
        <div className="dashboard-panel-md">
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h3>{t.errorTitle}</h3>
            <p>{error}</p>
            <button className="btn-md-primary" onClick={loadDashboardData}>
              {t.retry}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-overlay">
      <div className="dashboard-panel-md">
        {/* Header */}
        <div className="dashboard-header-md">
          <div className="header-content">
            <h1 className="dashboard-title">{t.title}</h1>
            <p className="dashboard-subtitle">{t.subtitle}</p>
          </div>
          <div className="header-actions-md">
            <button 
              className="export-btn-md" 
              onClick={handleExportPDF}
              disabled={exportLoading}
              title={t.exportPdf}
              data-testid="export-pdf-btn"
            >
              {exportLoading ? '⏳' : '📄'} PDF
            </button>
            <button 
              className="export-btn-md export-btn-excel"
              onClick={handleExportExcel}
              disabled={exportLoading}
              title={t.exportExcel}
              data-testid="export-excel-btn"
            >
              {exportLoading ? '⏳' : '📊'} Excel
            </button>
            <button className="close-btn-md" onClick={onClose} aria-label={t.close}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="dashboard-content-md">
          {/* Key Metrics Cards */}
          <div className="metrics-grid-md">
            <div className="metric-card-md primary" onClick={() => handleMetricClick('all')}>
              <div className="metric-icon-md">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7zm11 0h7v7h-7v-7z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div className="metric-data">
                <div className="metric-value-md">{stats?.total_events || 0}</div>
                <div className="metric-label-md">{t.totalEvents}</div>
              </div>
              <div className="metric-trend">↗</div>
            </div>

            <div className="metric-card-md success" onClick={() => handleMetricClick('active')}>
              <div className="metric-icon-md">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="metric-data">
                <div className="metric-value-md">{stats?.active_events || 0}</div>
                <div className="metric-label-md">{t.inProgress}</div>
              </div>
              <div className="metric-progress">
                <div className="progress-bar-md" style={{width: `${getResponseRate()}%`}}></div>
              </div>
            </div>

            <div className="metric-card-md warning" onClick={() => handleMetricClick('emergency')}>
              <div className="metric-icon-md">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 19h20L12 2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                  <path d="M12 9v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="metric-data">
                <div className="metric-value-md">{stats?.emergency_events || 0}</div>
                <div className="metric-label-md">{t.emergency}</div>
              </div>
              {stats?.emergency_events > 0 && <div className="metric-badge">{t.needsAttention}</div>}
            </div>

            <div className="metric-card-md info" onClick={() => handleMetricClick('completed')}>
              <div className="metric-icon-md">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="metric-data">
                <div className="metric-value-md">
                  {(stats?.status_distribution?.find(s => s.status === 'completed')?.count || 0) + 
                   (stats?.status_distribution?.find(s => s.status === 'final_completed')?.count || 0)}
                </div>
                <div className="metric-label-md">{t.completed}</div>
              </div>
              <div className="metric-percentage">{getCompletionRate()}%</div>
            </div>
          </div>

          {/* Analytics charts (Recharts) */}
          <AnalyticsCharts />

          {/* Status Distribution */}
          <div className="section-md">
            <h2 className="section-title-md">{t.statusDistribution}</h2>
            <div className="status-grid-md">
              {stats?.status_distribution?.map(item => (
                <div key={item.status} className="status-card-md">
                  <div className="status-header">
                    <div className="status-dot" style={{backgroundColor: getStatusColor(item.status)}}></div>
                    <span className="status-name">{STATUS_NAMES[item.status]?.[language] || item.status}</span>
                  </div>
                  <div className="status-value">{item.count}</div>
                  <div className="status-bar">
                    <div 
                      className="status-bar-fill" 
                      style={{
                        width: `${(item.count / (stats.total_events || 1)) * 100}%`,
                        backgroundColor: getStatusColor(item.status)
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Categories */}
          <div className="section-md">
            <h2 className="section-title-md">{t.topCategories}</h2>
            <div className="categories-grid-md">
              {stats?.category_distribution?.slice(0, 6).map(item => (
                <div 
                  key={item.category} 
                  className="category-card-md"
                  onClick={() => handleCategoryClick(item.category)}
                >
                  <div className="category-info">
                    <span className="category-icon">
                      {CATEGORY_NAMES[item.category]?.icon || '📍'}
                    </span>
                    <div className="category-details">
                      <div className="category-name">
                        {CATEGORY_NAMES[item.category]?.[language] || item.category}
                      </div>
                      <div className="category-count">{item.count} {t.eventsCount}</div>
                    </div>
                  </div>
                  <div className="category-arrow">→</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Events */}
          <div className="section-md">
            <h2 className="section-title-md">{t.recentEvents}</h2>
            <div className="events-list-md">
              {stats?.recent_events?.slice(0, 5).map(event => (
                <div 
                  key={event.id} 
                  className="event-item-md"
                  onClick={() => handleEventClick(event.id)}
                >
                  <div className="event-status-indicator" style={{backgroundColor: getStatusColor(event.status)}}></div>
                  <div className="event-content">
                    <div className="event-category-badge">
                      {CATEGORY_NAMES[event.category]?.icon || '📍'}
                      <span>{CATEGORY_NAMES[event.category]?.[language] || event.category}</span>
                    </div>
                    <div className="event-description">
                      {(language === 'kk' ? event.description_kk : event.description_ru) || event.description_ru || event.description_kk || t.noDescription}
                    </div>
                    <div className="event-meta">
                      <span className="event-time">{formatDate(event.created_at)}</span>
                      <span className="event-status-text">{STATUS_NAMES[event.status]?.[language] || event.status}</span>
                    </div>
                  </div>
                  <div className="event-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="section-md">
            <h2 className="section-title-md">{t.quickActions}</h2>
            <div className="actions-grid-md">
              <button className="action-btn-md" onClick={() => setShowEventManagement(true)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t.eventManagement}</span>
              </button>
              <button className="action-btn-md" onClick={() => setShowBlacklistManager(true)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M8.5 11a4 4 0 100-8 4 4 0 000 8zM23 11l-5 5M18 11l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t.blacklist}</span>
              </button>
              <button
                className="action-btn-md"
                onClick={() => setShowServiceManager(true)}
                data-testid="open-service-manager-btn"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t.serviceManagement}</span>
              </button>
              <button
                className="action-btn-md"
                onClick={() => setShowKeyManager(true)}
                data-testid="open-key-manager-btn"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t.serviceKeys}</span>
              </button>
              <button className="action-btn-md" onClick={() => handleMetricClick('all')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7zm11 0h7v7h-7v-7z" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>{t.allEvents}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showBlacklistManager && (
          <div className="modal-overlay" onClick={() => setShowBlacklistManager(false)}>
            <div className="modal-container-md" onClick={(e) => e.stopPropagation()}>
              <BlacklistManager onClose={() => setShowBlacklistManager(false)} />
            </div>
          </div>
        )}

        {showEventManagement && (
          <div className="modal-overlay" onClick={() => setShowEventManagement(false)}>
            <div className="modal-container-md" onClick={(e) => e.stopPropagation()}>
              <AdminEventManager onClose={() => setShowEventManagement(false)} />
            </div>
          </div>
        )}

        {showServiceManager && (
          <ServiceManager onClose={() => setShowServiceManager(false)} />
        )}

        {showKeyManager && (
          <ServiceKeyManager onClose={() => setShowKeyManager(false)} />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
